import React from 'react';
import { Platform, StyleProp, ViewStyle } from 'react-native';

import ExplorerList from '@components/Explorer/List';
import { useDebouncedMapRegionChange } from '@components/Explorer/MapBounds';
import { getCoordinateFitRegion } from '@components/Explorer/MapGeometry';
import ExplorerMapSheet from '@components/Explorer/MapSheet';
import type { MapMarkerProfile } from '@components/Explorer/Markers';
import {
  useExplorerGroupsQuery,
  useExplorerPlansQuery,
  useExplorerUsersQuery,
} from '@components/Explorer/Queries';
import { ExplorerSearchList } from '@components/Explorer/SearchList';
import type { ExplorerItem } from '@components/Explorer/explore';
import type { SearchTargets, SelectedSearchFilter } from '@components/Explorer/search';
import type { MapRegionChangeMeta } from '@components/Explorer/useExplorerMapRegionController';
import Placeholder from '@components/Placeholders';
import { useCurrentUser } from '@context/UsersContext';
import { sortBy } from '@helpers/Formatters';
import { useCurrentLocationCoordinate } from '@helpers/Location';
import { getMapRegion, isValidMapCoordinate } from '@helpers/Map';
import type { MapCoordinate, MapInitialRegion, MapRegion } from '@helpers/Map';
import { uniqueBy } from '@helpers/Parsers';
import type { OpenPlanOptions } from '@navigation/AppNavigation';
import type { Group, Plan, User } from '@types';

export type { SearchTargets };
export { useExplorerMapDataRefreshKey } from '@components/Explorer/useMapDataRefreshKey';

const QUERY_ID_SEPARATOR = '\n';
const QUERY_KEY_SEPARATOR = '\n\n';

type RegionQueryBoundsState = {
  setupKey: string;
  bounds?: MapRegion;
};

type ExplorerProps = {
  /** Plan ids removed from all loaded Explorer plan data. */
  excludedPlanIds?: string[];
  /** Overrides the avatar/name shown for plan markers. */
  getPlanDisplayProfile?: (plan: Plan) => MapMarkerProfile & { onPress?: () => void };
  /** Content rendered above Explorer search controls. */
  header?: React.ReactNode;
  /** Empty state for the plan list. Defaults to a "No plans" create-plan placeholder. */
  ListEmptyComponent?: React.ComponentProps<typeof ExplorerList>['ListEmptyComponent'];
  /** Explicit initial map region. If omitted, Explorer loads unbounded recent plans, then fits the map to loaded plan and residence coordinates. */
  initialRegion?: MapInitialRegion;
  /** Imperatively focuses the map on a region, for example after selecting a search place. */
  mapFocusRegion?: MapRegion;
  /** External loading state shown as a compact map overlay. */
  mapLoading?: boolean;
  /** Test id applied to the native map view. */
  mapTestID?: string;
  /** Opens a chat from plan card actions. */
  onChatPress: (chatId: string) => void;
  /** Opens the create-plan flow from empty states. */
  onCreatePlanPress: () => void;
  /** Opens a group from plan markers and search results. */
  onGroupPress: (groupId: string) => void;
  /** Called whenever the map region changes; user-driven changes also trigger debounced data loads. */
  onMapRegionChange?: (region: MapRegion, meta: MapRegionChangeMeta) => void;
  /** Optional screen refresh side effect, called before Explorer clears and reloads map data. */
  onRefresh?: () => void;
  /** Overrides the default plan marker/list press behavior. */
  onPlanPress?: (plan: Plan) => void;
  /** Opens a plan from default list and marker behavior. */
  onPlanNavigationPress: (planId: string, options?: OpenPlanOptions) => void;
  /** Receives active search filters whenever they change. */
  onSearchFiltersChange?: (filters: SelectedSearchFilter[]) => void;
  /** Overrides the default user marker press behavior. */
  onUserPress?: (user: User) => void;
  /** Opens a user from default marker behavior and plan card avatars. */
  onUserNavigationPress: (userId: string) => void;
  /** Changing this value clears loaded data and reruns the initial region query. */
  mapDataRefreshKey?: string | number;
  /** Group ids that can match plan queries and scoped group search. */
  queryGroupIds?: string[];
  /** User ids that can match plan queries and scoped user search. */
  queryUserIds?: string[];
  /** Pull-to-refresh state owned by the parent screen. Explorer also shows its own region loading. */
  refreshing?: boolean;
  /** Enables the user/group/place indexes that the search UI may query. */
  searchTargets: SearchTargets;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

export default function Explorer({
  excludedPlanIds,
  getPlanDisplayProfile,
  header,
  ListEmptyComponent,
  initialRegion,
  mapFocusRegion,
  mapLoading,
  mapTestID,
  onChatPress,
  onCreatePlanPress,
  onGroupPress,
  onMapRegionChange,
  onRefresh,
  onPlanPress,
  onPlanNavigationPress,
  onSearchFiltersChange,
  onUserPress,
  onUserNavigationPress,
  mapDataRefreshKey,
  queryGroupIds,
  queryUserIds,
  refreshing,
  searchTargets,
  style,
  testID,
}: ExplorerProps) {
  const currentUser = useCurrentUser();
  const { coordinate: currentLocationCoordinate } = useCurrentLocationCoordinate();
  const currentUserDoc = currentUser?.userDoc;
  const [plans, setPlans] = React.useState<Plan[]>([]);
  const [regionDataRefreshKey, setRegionDataRefreshKey] = React.useState(0);
  const [userDataRefreshKey, setUserDataRefreshKey] = React.useState(0);
  const queryUserIdsKey = getQueryIdsKey(queryUserIds);
  const queryGroupIdsKey = getQueryIdsKey(queryGroupIds);
  const excludedPlanIdsKey = getQueryIdsKey(excludedPlanIds);
  const resolvedQueryUserIds = React.useMemo(
    () => getQueryIdsFromKey(queryUserIdsKey),
    [queryUserIdsKey]
  );
  const resolvedQueryGroupIds = React.useMemo(
    () => getQueryIdsFromKey(queryGroupIdsKey),
    [queryGroupIdsKey]
  );
  const resolvedExcludedPlanIds = React.useMemo(
    () => getQueryIdsFromKey(excludedPlanIdsKey),
    [excludedPlanIdsKey]
  );
  const hasExplicitInitialRegion = isValidMapCoordinate(initialRegion);
  const resolvedInitialRegion = React.useMemo(
    () => getInitialRegion(initialRegion, currentLocationCoordinate, currentUserDoc?.hometown),
    [currentLocationCoordinate, currentUserDoc?.hometown, initialRegion]
  );
  const initialPlanQueryBounds = hasExplicitInitialRegion ? resolvedInitialRegion : undefined;
  const latestRegionQueryBoundsRef = React.useRef<MapRegion | undefined>(initialPlanQueryBounds);
  const hasUserControlledRegionRef = React.useRef(false);
  const [hasUserControlledRegion, setHasUserControlledRegion] = React.useState(false);
  const [regionQueryBoundsState, setRegionQueryBoundsState] =
    React.useState<RegionQueryBoundsState | null>(null);
  const regionQuerySetupKey = React.useMemo(
    () =>
      [
        queryGroupIdsKey,
        queryUserIdsKey,
        initialPlanQueryBounds ? getMapRegionKey(initialPlanQueryBounds) : '',
        mapDataRefreshKey ?? '',
      ].join(QUERY_KEY_SEPARATOR),
    [initialPlanQueryBounds, mapDataRefreshKey, queryGroupIdsKey, queryUserIdsKey]
  );
  const regionQueryBounds = regionQueryBoundsState?.bounds ?? initialPlanQueryBounds;
  const regionQueryEnabled = regionQueryBoundsState?.setupKey === regionQuerySetupKey;
  const regionPlansRefreshKey = React.useMemo(
    () => [mapDataRefreshKey ?? null, regionDataRefreshKey] as const,
    [mapDataRefreshKey, regionDataRefreshKey]
  );
  const usersRefreshKey = React.useMemo(
    () => [mapDataRefreshKey ?? null, userDataRefreshKey] as const,
    [mapDataRefreshKey, userDataRefreshKey]
  );
  const plansQuery = useExplorerPlansQuery({
    enabled: regionQueryEnabled,
    groupIds: resolvedQueryGroupIds,
    region: regionQueryBounds,
    refreshKey: regionPlansRefreshKey,
    userIds: resolvedQueryUserIds,
  });
  const usersQuery = useExplorerUsersQuery({
    refreshKey: usersRefreshKey,
    userIds: resolvedQueryUserIds,
  });
  const groupsQuery = useExplorerGroupsQuery({ groupIds: resolvedQueryGroupIds });
  const users = usersQuery.users;
  const groups = groupsQuery.groups;
  const groupsById = React.useMemo(
    () => new Map(groups.map((group) => [group.groupId, group])),
    [groups]
  );
  const resolvedGetPlanDisplayProfile = React.useCallback(
    (plan: Plan) =>
      getPlanDisplayProfile?.(plan) ||
      getDefaultPlanDisplayProfile({ onGroupPress, onUserNavigationPress, plan, groupsById }),
    [getPlanDisplayProfile, groupsById, onGroupPress, onUserNavigationPress]
  );
  const resolvedGetPlanGroups = React.useCallback(
    (plan: Plan) => getPlanGroups(plan, groupsById),
    [groupsById]
  );
  const resolvedOnPlanPress = React.useCallback(
    (plan: Plan) => {
      if (onPlanPress) {
        onPlanPress(plan);
        return;
      }

      onPlanNavigationPress(plan.planId);
    },
    [onPlanNavigationPress, onPlanPress]
  );
  const resolvedOnUserPress = React.useCallback(
    (user: User) => {
      if (onUserPress) {
        onUserPress(user);
        return;
      }

      onUserNavigationPress(user.userId);
    },
    [onUserNavigationPress, onUserPress]
  );
  const resolvedPlans = React.useMemo(() => {
    const excludedIds = new Set(resolvedExcludedPlanIds);
    return sortBy(
      uniqueBy(plans, 'planId').filter((plan) => !excludedIds.has(plan.planId)),
      'createdAt',
      'desc'
    );
  }, [plans, resolvedExcludedPlanIds]);
  const autoFitMapFocusRegion = React.useMemo(() => {
    if (hasExplicitInitialRegion || hasUserControlledRegion) return undefined;

    return getCoordinateFitRegion([
      ...resolvedPlans.map((plan) => plan.location),
      ...users.map((user) => user.hometown),
    ]);
  }, [hasExplicitInitialRegion, hasUserControlledRegion, resolvedPlans, users]);
  const resolvedMapFocusRegion = mapFocusRegion ?? autoFitMapFocusRegion;
  const clearRegionData = React.useCallback(() => {
    setPlans([]);
  }, []);

  const requestRegionData = React.useCallback(
    (region: MapRegion) => {
      latestRegionQueryBoundsRef.current = region;
      setRegionQueryBoundsState({ setupKey: regionQuerySetupKey, bounds: region });
    },
    [regionQuerySetupKey]
  );
  const debouncedLoadRegionData = useDebouncedMapRegionChange(requestRegionData);
  const resolvedOnMapRegionChange = React.useCallback(
    (region: MapRegion, meta: MapRegionChangeMeta) => {
      latestRegionQueryBoundsRef.current = region;
      onMapRegionChange?.(region, meta);
      if (meta.userInteraction) {
        hasUserControlledRegionRef.current = true;
        setHasUserControlledRegion(true);
        debouncedLoadRegionData(region);
      }
    },
    [debouncedLoadRegionData, onMapRegionChange]
  );
  const resolvedOnRefresh = React.useCallback(() => {
    onRefresh?.();
    clearRegionData();
    setRegionDataRefreshKey((key) => key + 1);
    setUserDataRefreshKey((key) => key + 1);
    setRegionQueryBoundsState({
      setupKey: regionQuerySetupKey,
      bounds: latestRegionQueryBoundsRef.current,
    });
  }, [clearRegionData, onRefresh, regionQuerySetupKey]);

  const defaultListEmptyComponent = (
    <Placeholder
      title="No plans"
      icon="sign-direction"
      action="Add your plans"
      onPress={onCreatePlanPress}
    />
  );

  React.useEffect(() => {
    if (plansQuery.dataUpdatedAt === 0) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPlans(plansQuery.plans);
  }, [plansQuery.dataUpdatedAt, plansQuery.plans]);

  React.useEffect(() => {
    const nextRegionQueryBounds = hasUserControlledRegionRef.current
      ? latestRegionQueryBoundsRef.current
      : initialPlanQueryBounds;

    // Explorer intentionally clears stale plan data before starting the next initial query.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    clearRegionData();
    latestRegionQueryBoundsRef.current = nextRegionQueryBounds;
    setRegionQueryBoundsState({
      setupKey: regionQuerySetupKey,
      bounds: nextRegionQueryBounds,
    });
  }, [clearRegionData, initialPlanQueryBounds, regionQuerySetupKey]);

  const renderContent = (items: ExplorerItem[]) => (
    <ExplorerList
      items={items}
      getPlanGroups={resolvedGetPlanGroups}
      ListEmptyComponent={ListEmptyComponent ?? defaultListEmptyComponent}
      onRefresh={resolvedOnRefresh}
      onUserPress={resolvedOnUserPress}
      planCardNavigation={{
        onChatPress,
        onPlanPress: onPlanNavigationPress,
        onUserPress: onUserNavigationPress,
      }}
      participantUsers={users}
      refreshing={refreshing || plansQuery.loading || usersQuery.loading}
      style={style}
      testID={testID}
    />
  );

  if (Platform.OS === 'web') {
    return (
      <ExplorerSearchList
        plans={resolvedPlans}
        users={users}
        groups={groups}
        searchGroupIds={resolvedQueryGroupIds}
        searchUserIds={resolvedQueryUserIds}
        header={header}
        getPlanDisplayProfile={resolvedGetPlanDisplayProfile}
        initialRegion={resolvedInitialRegion}
        onSearchFiltersChange={onSearchFiltersChange}
        renderContent={renderContent}
        searchTargets={searchTargets}
      />
    );
  }

  return (
    <ExplorerMapSheet
      plans={resolvedPlans}
      users={users}
      groups={groups}
      searchGroupIds={resolvedQueryGroupIds}
      searchUserIds={resolvedQueryUserIds}
      getPlanDisplayProfile={resolvedGetPlanDisplayProfile}
      header={header}
      initialRegion={resolvedInitialRegion}
      mapFocusRegion={resolvedMapFocusRegion}
      mapLoading={mapLoading}
      mapTestID={mapTestID}
      onMapRegionChange={resolvedOnMapRegionChange}
      onPlanPress={resolvedOnPlanPress}
      onSearchFiltersChange={onSearchFiltersChange}
      onUserPress={resolvedOnUserPress}
      renderContent={renderContent}
      searchTargets={searchTargets}
    />
  );
}

function getDefaultPlanDisplayProfile({
  plan,
  groupsById,
  onGroupPress,
  onUserNavigationPress,
}: {
  plan: Plan;
  groupsById: Map<string, Group>;
  onGroupPress: (groupId: string) => void;
  onUserNavigationPress: (userId: string) => void;
}): MapMarkerProfile & { onPress?: () => void } {
  const group = getPlanDisplayGroup(plan, groupsById);

  if (group) {
    return {
      displayName: group.displayName,
      entityType: 'group',
      photoUrl: group.photoUrl,
      onPress: () => onGroupPress(group.groupId),
    };
  }

  return {
    displayName: plan.user.displayName,
    entityType: 'user',
    photoUrl: plan.user.photoUrl,
    onPress: () => onUserNavigationPress(plan.userId),
  };
}

function getPlanDisplayGroup(plan: Plan, groupsById: Map<string, Group>) {
  const groupId = plan.groupIds?.find((groupId) => groupsById.has(groupId));
  return groupId ? groupsById.get(groupId) : undefined;
}

function getPlanGroups(plan: Plan, groupsById: Map<string, Group>) {
  return (plan.groupIds || []).flatMap((groupId) => {
    const group = groupsById.get(groupId);
    return group
      ? [{ groupId: group.groupId, displayName: group.displayName, photoUrl: group.photoUrl }]
      : [];
  });
}

function getInitialRegion(
  initialRegion?: MapInitialRegion,
  ...fallbackCoordinates: (MapCoordinate | undefined)[]
) {
  if (isValidMapCoordinate(initialRegion)) return getMapRegion(initialRegion);

  return getMapRegion(fallbackCoordinates.find(isValidMapCoordinate));
}

function getQueryIdsKey(ids: string[] | undefined) {
  return [...new Set(ids ?? [])]
    .filter((id) => id.length > 0)
    .sort()
    .join(QUERY_ID_SEPARATOR);
}

function getQueryIdsFromKey(key: string) {
  return key.length > 0 ? key.split(QUERY_ID_SEPARATOR) : [];
}

function getMapRegionKey(region: MapRegion) {
  return [region.latitude, region.longitude, region.latitudeDelta, region.longitudeDelta].join(
    QUERY_ID_SEPARATOR
  );
}
